import { convertUTCtoKST } from 'utils/date';

/**
 * 산정 기간·지급 예정일 표기 (#2182).
 *
 * 별지기 MG 는 KST 월력 기준으로 산정되는데 리워드 페이지는 해외에서도 열린다.
 * 로컬 타임존 게터(getFullYear 등)로 찍으면 보는 사람마다 날짜가 달라져 CS 가 들어오므로
 * 여기서는 항상 KST 로 고정 표기한다.
 */

const KST_LABEL = 'KST';

/** BE 가 내려주는 지급 예정일(명목일). 시각이 없는 순수 날짜만 받는다 */
const PLAIN_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * UTC ISO — 오프셋(`Z` 또는 `±hh:mm`)이 명시된 date-time 만 받는다.
 * 오프셋이 없으면 ES 스펙상 로컬 타임존으로 해석돼 보는 사람마다 날짜가 달라진다.
 */
const OFFSET_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/;

const isParsableDate = (value: string) => OFFSET_ISO_PATTERN.test(value) && !Number.isNaN(new Date(value).getTime());

/**
 * UTC ISO 문자열을 KST `YYYY.MM.DD HH:mm` 으로 표기한다.
 * 해석할 수 없는 값이면 null — 호출부가 해당 줄을 통째로 감춘다.
 */
export const formatKstDateTime = (utcIso: string): string | null => {
  if (!isParsableDate(utcIso)) return null;

  // convertUTCtoKST 는 '2026-09-01T00:00:00.000+09:00' 형태를 준다.
  // 이걸 다시 Date 로 되돌리면 로컬 타임존으로 밀리므로 문자열에서 그대로 잘라 쓴다.
  const [date, time] = convertUTCtoKST(utcIso).split('T');

  return `${date.replace(/-/g, '.')} ${time.slice(0, 5)}`;
};

/** 산정 기간 표기 — `2026.09.01 00:00 ~ 2026.09.30 23:59 (KST)` */
export const formatSettlementPeriod = (startAt: string, endAt: string): string | null => {
  const start = formatKstDateTime(startAt);
  const end = formatKstDateTime(endAt);

  if (!start || !end) return null;

  return `${start} ~ ${end} (${KST_LABEL})`;
};

/**
 * 지급 예정일 표기 — `2026.10.10`.
 * 주말·공휴일 자동 보정은 없다 — 명목일을 그대로 표시하고 변경 가능성은 안내 문구가 알린다.
 * FE 는 어느 경우에도 날짜를 계산하지 않는다.
 */
export const formatPayoutDate = (payoutDate: string): string | null =>
  PLAIN_DATE_PATTERN.test(payoutDate) ? payoutDate.replace(/-/g, '.') : null;
